import React from 'react';
import { Icon, Loader, Popup } from 'semantic-ui-react';
import { SemanticICONS } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import CurrencyHelper from '../../../helpers/currencyHelper';
import styles from './MetricWidget.module.css';

interface MetricWidgetProps {
  title: string;
  value?: string | number;
  change?: number;
  previousValue?: number;
  period?: string;
  isLoading?: boolean;
  icon?: SemanticICONS;
  currency?: string;
  isCurrency?: boolean;
}

export const MetricWidget: React.FC<MetricWidgetProps> = ({
  title,
  value = 0,
  change = 0,
  previousValue = 0,
  period = 'previous period',
  isLoading = false,
  icon,
  currency = 'aud',
  isCurrency = false
}) => {
  const renderChangeIndicator = () => {
    if (isLoading || change === 0) return null;
    
    const isPositive = change > 0;
    const changeClass = isPositive ? styles['metricChangePositive'] : styles['metricChangeNegative'];
    const changeIcon = isPositive ? 'arrow up' : 'arrow down';
    
    // Format previous value based on whether it's currency or not
    const formatPreviousValue = () => {
      if (typeof previousValue === 'number') {
        if (isCurrency) {
          // Round currency value to remove decimals
          const roundedValue = Math.round(previousValue);
          return CurrencyHelper.formatCurrency(roundedValue, currency);
        }
        return Math.round(previousValue).toLocaleString();
      }
      return previousValue;
    };
    
    const tooltipContent = (
      <div>
        <div>{I18n.t('home.metrics.comparedTo', { period })}</div>
        <div><strong>{formatPreviousValue()}</strong></div>
      </div>
    );
    
    return (
      <Popup
        trigger={
          <div className={`${styles['metricChange']} ${changeClass}`}>
            <Icon name={changeIcon} size="small" />
            <span>{Math.abs(change).toFixed(0)}%</span>
          </div>
        }
        content={tooltipContent}
        position="top center"
        size="small"
      />
    );
  };

  // Format value without decimals
  const formatValue = (val: string | number) => {
    if (typeof val === 'string') return val;
    return Math.round(val).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className={`${styles['metricWidgetContainer']} ${styles['metricWidgetLoading']}`}>
        <div className={styles['metricCard']}>
          <div className={styles['metricCardContent']}>
            <div className={styles['metricHeader']}>
              <span className={styles['metricTitle']}>{title}</span>
            </div>
            <div className={styles['metricValueContainer']}>
              <Loader active inline size="small" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['metricWidgetContainer']}>
      <div className={styles['metricCard']}>
        <div className={styles['metricCardContent']}>
          <div className={styles['metricHeader']}>
            {icon && <Icon name={icon} className={styles['metricIcon']} />}
            <span className={styles['metricTitle']}>{title}</span>
          </div>
          <div className={styles['metricValueContainer']}>
            <div className={styles['metricValue']}>
              {formatValue(value)}
            </div>
            {renderChangeIndicator()}
          </div>
        </div>
      </div>
    </div>
  );
};
